import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Button,
  CircularProgress,
  Alert,
  List,
  ListItem,
  ListItemText,
  Divider,
  Chip,
  Grid,
  IconButton,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CalendarToday as CalendarIcon,
  Book as BookIcon,
  Keyboard as KeyboardIcon,
  Speed as SpeedIcon,
  Assignment as AssignmentIcon,
} from '@mui/icons-material';
import axios from 'axios';
import { toast } from 'react-toastify';

const getApiBaseUrl = () => {
  if (process.env.REACT_APP_API_URL) {
    return process.env.REACT_APP_API_URL;
  }
  if (typeof window !== 'undefined' && (window.location.hostname.includes('ondigitalocean.app') || window.location.hostname.includes('services.knls.in'))) {
    return '/api';
  }
  return 'http://localhost:3000/api';
};

const API_BASE_URL = getApiBaseUrl();

const TrackSheetHistory = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [trackSheets, setTrackSheets] = useState([]);
  const [selectedSheet, setSelectedSheet] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  useEffect(() => {
    const token = localStorage.getItem('student_token');
    if (!token) {
      navigate('/student/login');
      return;
    }

    loadHistory();
  }, [navigate]);

  const loadHistory = async () => {
    try {
      setLoading(true);
      const token = localStorage.getItem('student_token');
      const response = await axios.get(`${API_BASE_URL}/track-sheets/history`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success) {
        setTrackSheets(response.data.trackSheets);
      }
    } catch (error) {
      console.error('Error loading history:', error);
      toast.error('Failed to load track sheet history');
    } finally {
      setLoading(false);
    }
  };

  const loadSheetDetails = async (date) => {
    try {
      setLoadingDetails(true);
      const token = localStorage.getItem('student_token');
      const response = await axios.get(`${API_BASE_URL}/track-sheets/${date}`, {
        headers: { Authorization: `Bearer ${token}` },
      });

      if (response.data.success && response.data.trackSheet) {
        setSelectedSheet(response.data.trackSheet);
      }
    } catch (error) {
      console.error('Error loading sheet details:', error);
      toast.error('Failed to load track sheet details');
    } finally {
      setLoadingDetails(false);
    }
  };

  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f5f5f5', py: 3 }}>
      <Box sx={{ maxWidth: 1200, mx: 'auto', px: 2 }}>
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              <IconButton onClick={() => navigate('/student/track-sheet')}>
                <ArrowBackIcon />
              </IconButton>
              <Typography variant="h5" fontWeight="bold">
                Track Sheet History
              </Typography>
            </Box>
          </CardContent>
        </Card>

        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Previous Track Sheets
                </Typography>
                <Divider sx={{ mb: 2 }} />
                {trackSheets.length === 0 ? (
                  <Alert severity="info">No track sheets found</Alert>
                ) : (
                  <List>
                    {trackSheets.map((sheet, index) => (
                      <React.Fragment key={sheet.id}>
                        <ListItem
                          button
                          onClick={() => loadSheetDetails(sheet.track_date)}
                          sx={{
                            bgcolor: selectedSheet?.track_date === sheet.track_date ? '#e3f2fd' : 'transparent',
                            borderRadius: 1,
                            mb: 1,
                          }}
                        >
                          <ListItemText
                            primary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CalendarIcon fontSize="small" color="primary" />
                                <Typography variant="body1" fontWeight="medium">
                                  {formatDate(sheet.track_date)}
                                </Typography>
                              </Box>
                            }
                            secondary={
                              <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                                <Chip label={`${sheet.lesson_count || 0} Lessons`} size="small" />
                                <Chip label={`${sheet.shortcut_count || 0} Shortcuts`} size="small" />
                                <Chip label={`${sheet.exam_count || 0} Exams`} size="small" />
                              </Box>
                            }
                          />
                        </ListItem>
                        {index < trackSheets.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Grid>

          <Grid item xs={12} md={8}>
            {loadingDetails ? (
              <Card>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <CircularProgress />
                </CardContent>
              </Card>
            ) : selectedSheet ? (
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Track Sheet - {formatDate(selectedSheet.track_date)}
                  </Typography>
                  <Divider sx={{ mb: 2 }} />

                  {/* Lesson Details */}
                  {selectedSheet.lessonDetails && selectedSheet.lessonDetails.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <BookIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="subtitle1" fontWeight="bold">
                          Lesson Details
                        </Typography>
                      </Box>
                      {selectedSheet.lessonDetails.map((lesson, index) => (
                        <Box key={index} sx={{ ml: 4, mb: 2 }}>
                          <Typography variant="body1" fontWeight="medium" gutterBottom>
                            {lesson.lesson_name}
                          </Typography>
                          {lesson.points && lesson.points.length > 0 && (
                            <List dense>
                              {lesson.points.map((point, pIndex) => (
                                <ListItem key={pIndex}>
                                  <ListItemText
                                    primary={`• ${point.point_text}`}
                                    primaryTypographyProps={{ variant: 'body2' }}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}

                  {/* Shortcuts */}
                  {selectedSheet.shortcuts && selectedSheet.shortcuts.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <KeyboardIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="subtitle1" fontWeight="bold">
                          Shortcuts
                        </Typography>
                      </Box>
                      <List dense>
                        {selectedSheet.shortcuts.map((shortcut, index) => (
                          <ListItem key={index}>
                            <ListItemText
                              primary={`${shortcut.shortcut_key} - ${shortcut.use_case}`}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {/* Speed Practice */}
                  {selectedSheet.speedPractice && selectedSheet.speedPractice.length > 0 && (
                    <Box sx={{ mb: 3 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <SpeedIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="subtitle1" fontWeight="bold">
                          Speed Practice
                        </Typography>
                      </Box>
                      <List dense>
                        {selectedSheet.speedPractice.map((practice, index) => (
                          <ListItem key={index}>
                            <ListItemText
                              primary={`${practice.practice_type} - ${practice.lesson_name} - ${practice.speed}`}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {/* Exam Results */}
                  {selectedSheet.examResults && selectedSheet.examResults.length > 0 && (
                    <Box>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <AssignmentIcon color="primary" sx={{ mr: 1 }} />
                        <Typography variant="subtitle1" fontWeight="bold">
                          Exam Results
                        </Typography>
                      </Box>
                      <List dense>
                        {selectedSheet.examResults.map((exam, index) => (
                          <ListItem key={index}>
                            <ListItemText
                              primary={`${exam.exam_name}: ${exam.obtained_marks}/${exam.total_marks} (${exam.percentage.toFixed(2)}%)`}
                              primaryTypographyProps={{ variant: 'body2' }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </Box>
                  )}

                  {!selectedSheet.lessonDetails?.length &&
                    !selectedSheet.shortcuts?.length &&
                    !selectedSheet.speedPractice?.length &&
                    !selectedSheet.examResults?.length && (
                      <Alert severity="info">No data recorded for this date</Alert>
                    )}
                </CardContent>
              </Card>
            ) : (
              <Card>
                <CardContent>
                  <Alert severity="info">Select a date to view track sheet details</Alert>
                </CardContent>
              </Card>
            )}
          </Grid>
        </Grid>
      </Box>
    </Box>
  );
};

export default TrackSheetHistory;





